import { Component, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngxs/store';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { Timesheet } from 'src/app/models/Timesheet';
import { UpdateRequiredHours, FetchTimesheets } from '../timesheets.actions';
// Parse CSV uploads from SDWorx; no external CSV lib needed

interface ParsedEntry {
  firstName?: string;
  lastName?: string;
  fullName?: string;
  hours?: number;
  raw?: any;
}

@Component({
  selector: 'app-sdworx-upload-modal',
  templateUrl: './sdworx-upload-modal.component.html',
  styleUrls: ['./sdworx-upload-modal.component.scss']
})
export class SdworxUploadModalComponent {
  @Input() timesheets: Timesheet[] = [];
  @Input() selectedDateLabel: string = '';

  fileName: string = '';
  parsed: ParsedEntry[] = [];
  unmatched: ParsedEntry[] = [];
  matched: Array<{ entry: ParsedEntry; timesheet: Timesheet; originalHours: number; newHours: number }> = [];
  matchedChanged: Array<{ entry: ParsedEntry; timesheet: Timesheet; originalHours: number; newHours: number }> = [];
  matchedUnchanged: Array<{ entry: ParsedEntry; timesheet: Timesheet; originalHours: number; newHours: number }> = [];

  isParsing: boolean = false;
  proceedAsked: boolean = false;
  // CSV rows and header-derived mapping
  rows: string[][] = [];
  headers: string[] = [];
  headerIdx: number = 0;
  selectedFirstCol: number = -1;
  selectedLastCol: number = -1;
  selectedNameSingleCol: number = -1;
  selectedHoursCol: number = -1;

  constructor(
    public activeModal: NgbActiveModal,
    private store: Store,
    private toastr: ToastrService,
    private logger: NGXLogger,
  ) {}

  onFileSelected(event: any): void {
    try {
      const file: File = event?.target?.files?.[0];
      if (!file) return;
      this.fileName = file.name;
      const reader = new FileReader();
      reader.onload = (e: any) => {
        try {
          this.isParsing = true;
          const text: string = e.target.result as string;
          const rows: string[][] = this.parseCsv(text);
          this.rows = rows;
          this.detectColumnsFromRows(rows);
          this.headers = rows[this.headerIdx] || (rows[0] || []).map(c => String(c));
          // Preselect detected columns
          this.selectedFirstCol = this._detectedFirstCol;
          this.selectedLastCol = this._detectedLastCol;
          this.selectedNameSingleCol = this._detectedNameSingleCol;
          this.selectedHoursCol = this._detectedHoursCol;
          this.applyMappingAndMatch();
        } catch (err) {
          this.logger.error('Failed to parse XLS', err);
          this.toastr.error('Kon het CSV rapport niet lezen.');
        } finally {
          this.isParsing = false;
        }
      };
      reader.readAsText(file);
    } catch (err) {
      this.logger.error('onFileSelected failed', err);
    }
  }

  private parseCsv(text: string): string[][] {
    const rows: string[][] = [];
    let field = '';
    let row: string[] = [];
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const ch = text[i];
      if (inQuotes) {
        if (ch === '"') {
          const next = text[i + 1];
          if (next === '"') { // escaped quote
            field += '"';
            i++;
          } else {
            inQuotes = false;
          }
        } else {
          field += ch;
        }
      } else {
        if (ch === '"') {
          inQuotes = true;
        } else if (ch === ',') {
          row.push(field);
          field = '';
        } else if (ch === '\n' || ch === '\r') {
          // Finish row on newline; avoid pushing empty trailing CRLF
          if (ch === '\r' && text[i + 1] === '\n') {
            // consume next \n
          }
          row.push(field);
          // Trim trailing empties? keep structure
          rows.push(row);
          row = [];
          field = '';
          // Skip \n in CRLF
          if (ch === '\r' && text[i + 1] === '\n') i++;
        } else {
          field += ch;
        }
      }
    }
    // push last field/row if present
    if (field.length > 0 || row.length > 0) {
      row.push(field);
      rows.push(row);
    }
    return rows.map(r => r.map(v => (v ?? '').trim()));
  }

  private normalize(s?: string): string {
    return (s || '').toString().trim().toLowerCase();
  }

  private _detectedHoursCol: number = -1;
  private _detectedFirstCol: number = -1;
  private _detectedLastCol: number = -1;
  private _detectedNameSingleCol: number = -1;

  private detectColumnsFromRows(rows: string[][]): void {
    this.headerIdx = 0;
    this._detectedHoursCol = -1;
    this._detectedFirstCol = -1;
    this._detectedLastCol = -1;
    this._detectedNameSingleCol = -1;

    const norm = (v: any) => this.normalize(String(v ?? ''));
    const maxScan = Math.min(rows.length, 40);
    for (let i = 0; i < maxScan; i++) {
      const row = rows[i] || [];
      for (let j = 0; j < row.length; j++) {
        const cell = norm(row[j]);
        if (this._detectedHoursCol < 0 && ((cell.includes('totaal') && cell.includes('prest')) || cell.includes('totaal prestaties') || (cell.includes('total') && cell.includes('hours')))) {
          this._detectedHoursCol = j; this.headerIdx = i;
        }
        if (this._detectedFirstCol < 0 && (cell.includes('voornaam') || cell === 'first name' || cell.includes('first'))) {
          this._detectedFirstCol = j; this.headerIdx = i;
        }
        // Prefer explicit last-name headers; only use a single 'naam' column as fallback (avoid matching 'voornaam')
        if (this._detectedLastCol < 0 && (cell.includes('familienaam') || cell.includes('achternaam') || cell === 'naam' || cell === 'last name' || cell.includes('last'))) {
          this._detectedLastCol = j; this.headerIdx = i;
        }
        if (this._detectedNameSingleCol < 0 && (cell === 'naam' || cell.includes('name'))) {
          this._detectedNameSingleCol = j; this.headerIdx = i;
        }
      }
      if ((this._detectedHoursCol >= 0) && ((this._detectedFirstCol >= 0 && this._detectedLastCol >= 0) || this._detectedNameSingleCol >= 0)) break;
    }
    // Fallback heuristics for hours/name
    const startIdx = this.headerIdx + 1;
    if (this._detectedHoursCol < 0) {
      const counts: number[] = [];
      const cols = Math.max(...rows.map(r => r.length));
      for (let j = 0; j < cols; j++) {
        let c = 0;
        for (let i = startIdx; i < Math.min(rows.length, startIdx + 100); i++) {
          const v = String(rows[i]?.[j] ?? '').trim();
          if (!v) continue;
          // Keep decimal dots; only normalize decimal commas to dots and strip NBSP/spaces
          const n = Number(v.replace(/\u00A0/g, '').replace(/\s/g, '').replace(',', '.'));
          if (!isNaN(n)) c++;
        }
        counts[j] = c;
      }
      this._detectedHoursCol = counts.indexOf(Math.max(...counts));
    }
    if (this._detectedFirstCol < 0 && this._detectedLastCol < 0 && this._detectedNameSingleCol < 0) {
      const cols = Math.max(...rows.map(r => r.length));
      let bestCol = -1, bestScore = -1;
      for (let j = 0; j < cols; j++) {
        let score = 0;
        for (let i = startIdx; i < Math.min(rows.length, startIdx + 100); i++) {
          const v = String(rows[i]?.[j] ?? '').trim();
          if (!v) continue;
          if (/[A-Za-zÀ-ÿ]/.test(v) && v.includes(' ')) score++;
        }
        if (score > bestScore) { bestScore = score; bestCol = j; }
      }
      this._detectedNameSingleCol = bestCol;
    }
  }

  private applyMappingAndMatch(): void {
    this.parsed = this.extractEntries(this.rows, {
      headerIdx: this.headerIdx,
      hoursCol: this.selectedHoursCol,
      firstCol: this.selectedFirstCol,
      lastCol: this.selectedLastCol,
      nameSingleCol: this.selectedNameSingleCol,
    });
    this.matchEntries();
  }

  onHeaderMappingChanged(kind: 'first' | 'last' | 'name' | 'hours', idx: any): void {
    const val = Number(idx);
    if (kind === 'first') this.selectedFirstCol = val;
    if (kind === 'last') this.selectedLastCol = val;
    if (kind === 'name') this.selectedNameSingleCol = val;
    if (kind === 'hours') this.selectedHoursCol = val;
    this.applyMappingAndMatch();
  }

  private extractEntries(rows: any[][], mapping?: { headerIdx?: number; hoursCol?: number; firstCol?: number; lastCol?: number; nameSingleCol?: number }): ParsedEntry[] {
    const entries: ParsedEntry[] = [];
    if (!Array.isArray(rows) || rows.length === 0) return entries;

    const norm = (v: any) => this.normalize(String(v ?? ''));

    // Try to detect header row and relevant column indices (or use mapping)
    let headerIdx = typeof mapping?.headerIdx === 'number' ? mapping!.headerIdx! : 0;
    let hoursCol = typeof mapping?.hoursCol === 'number' && mapping!.hoursCol! >= 0 ? mapping!.hoursCol! : -1;
    let firstCol = typeof mapping?.firstCol === 'number' && mapping!.firstCol! >= 0 ? mapping!.firstCol! : -1;
    let lastCol = typeof mapping?.lastCol === 'number' && mapping!.lastCol! >= 0 ? mapping!.lastCol! : -1;
    let nameSingleCol = typeof mapping?.nameSingleCol === 'number' && mapping!.nameSingleCol! >= 0 ? mapping!.nameSingleCol! : -1;

    if (hoursCol < 0 || ((firstCol < 0 || lastCol < 0) && nameSingleCol < 0)) {
      const maxScan = Math.min(rows.length, 40);
      for (let i = 0; i < maxScan; i++) {
        const row = rows[i] || [];
        for (let j = 0; j < row.length; j++) {
          const cell = norm(row[j]);
          if (hoursCol < 0 && ((cell.includes('totaal') && cell.includes('prest')) || cell.includes('totaal prestaties') || (cell.includes('total') && cell.includes('hours')))) {
            hoursCol = j; headerIdx = i;
          }
          if (firstCol < 0 && (cell.includes('voornaam') || cell === 'first name' || cell.includes('first'))) {
            firstCol = j; headerIdx = i;
          }
          if (lastCol < 0 && (cell.includes('familienaam') || cell.includes('achternaam') || cell === 'naam' || cell === 'last name' || cell.includes('last'))) {
            lastCol = j; headerIdx = i;
          }
          if (nameSingleCol < 0 && (cell === 'naam' || cell.includes('name'))) {
            nameSingleCol = j; headerIdx = i;
          }
        }
        if ((hoursCol >= 0) && ((firstCol >= 0 && lastCol >= 0) || nameSingleCol >= 0)) break;
      }
    }

    // If hours/name columns not found, use heuristics across next rows
    const startIdx = headerIdx + 1;
    if (hoursCol < 0) {
      // Choose the column with most numeric-looking values in subsequent rows
      const counts: number[] = [];
      const cols = Math.max(...rows.map(r => r.length));
      for (let j = 0; j < cols; j++) {
        let c = 0;
        for (let i = startIdx; i < Math.min(rows.length, startIdx + 100); i++) {
          const v = String(rows[i]?.[j] ?? '').trim();
          if (!v) continue;
          const n = Number(v.replace(',', '.'));
          if (!isNaN(n)) c++;
        }
        counts[j] = c;
      }
      hoursCol = counts.indexOf(Math.max(...counts));
    }

    if (firstCol < 0 && lastCol < 0 && nameSingleCol < 0) {
      // Look for a single name column: many alphabetic values with spaces
      const cols = Math.max(...rows.map(r => r.length));
      let bestCol = -1, bestScore = -1;
      for (let j = 0; j < cols; j++) {
        let score = 0;
        for (let i = startIdx; i < Math.min(rows.length, startIdx + 100); i++) {
          const v = String(rows[i]?.[j] ?? '').trim();
          if (!v) continue;
          if (/[A-Za-zÀ-ÿ]/.test(v) && v.includes(' ')) score++;
        }
        if (score > bestScore) { bestScore = score; bestCol = j; }
      }
      nameSingleCol = bestCol;
    }

    for (let i = startIdx; i < rows.length; i++) {
      const row = rows[i] || [];
      let firstName = '';
      let lastName = '';

      if (firstCol >= 0 && lastCol >= 0) {
        firstName = String(row[firstCol] ?? '').trim();
        lastName = String(row[lastCol] ?? '').trim();
      } else if (nameSingleCol >= 0) {
        const full = String(row[nameSingleCol] ?? '').trim();
        if (!full) continue;
        if (full.includes(',')) {
          const parts = full.split(',').map(p => p.trim());
          lastName = parts[0] || '';
          firstName = parts[1] || '';
        } else {
          const parts = full.split(' ').map(p => p.trim()).filter(p => !!p);
          if (parts.length >= 2) {
            firstName = parts[0];
            lastName = parts.slice(1).join(' ');
          } else {
            firstName = full; // fallback, no last name
          }
        }
      }

      let val = String(row[hoursCol] ?? '').trim();
      // Clean NBSP thousands separators from CSV export
      val = val.replace(/\u00A0/g, '').replace(/\s/g, '');
      if (!val && !firstName && !lastName) continue;
      // Preserve decimal dot. Only convert comma-decimals to dots.
      const normalized = val.replace(',', '.');
      const hoursNum = Number(normalized);
      if ((firstName || lastName) && !isNaN(hoursNum)) {
        entries.push({ firstName, lastName, fullName: `${firstName} ${lastName}`.trim(), hours: hoursNum, raw: row });
      }
    }
    return entries;
  }

  private matchEntries(): void {
    this.unmatched = [];
    this.matched = [];
    this.matchedChanged = [];
    this.matchedUnchanged = [];

    // Build timesheet lookup by normalized names
    const tsByName = new Map<string, Timesheet>();
    (this.timesheets || []).forEach(ts => {
      const f = this.normalize(ts?.employee?.firstName);
      const l = this.normalize(ts?.employee?.lastName);
      const full1 = `${f} ${l}`.trim();
      const full2 = `${l} ${f}`.trim();
      if (full1) tsByName.set(full1, ts);
      if (full2) tsByName.set(full2, ts);
    });

    for (const e of this.parsed) {
      const f = this.normalize(e.firstName);
      const l = this.normalize(e.lastName);
      const full = `${f} ${l}`.trim();
      const fullRev = `${l} ${f}`.trim();
      const ts = tsByName.get(full) || tsByName.get(fullRev);
      if (!ts) {
        this.unmatched.push(e);
      } else {
        const orig = ts.hoursRequired || 0;
        const next = Number(e.hours || 0);
        const item = { entry: e, timesheet: ts, originalHours: orig, newHours: next };
        this.matched.push(item);
        if ((orig ?? 0) !== (next ?? 0)) {
          this.matchedChanged.push(item);
        } else {
          this.matchedUnchanged.push(item);
        }
      }
    }
  }

  applyUpdates(): void {
    if (this.unmatched.length > 0 && !this.proceedAsked) {
      this.proceedAsked = true;
      this.toastr.warning('Sommige namen konden niet gematcht worden. Klik nogmaals op "Toepassen" om toch verder te gaan.');
      return;
    }
    const toChange = this.matchedChanged;
    const updates = toChange.map(m => this.store.dispatch(new UpdateRequiredHours(m.timesheet.uuid!, m.newHours)));
    if (updates.length === 0) {
      this.toastr.info('Geen wijzigingen doorgevoerd.');
      this.activeModal.close();
      return;
    }
    Promise.all(updates.map(o => o.toPromise())).then(() => {
      const changed = toChange;
      if (changed.length > 0) {
        const summary = changed.map(m => `${m.timesheet.employee?.firstName} ${m.timesheet.employee?.lastName}: ${m.originalHours ?? 0} → ${m.newHours ?? 0}`).join('\n');
        this.toastr.success('Required hours bijgewerkt voor:\n' + summary);
      } else {
        this.toastr.info('Geen wijzigingen in required hours (waarden waren identiek).');
      }
      this.store.dispatch(new FetchTimesheets());
      this.activeModal.close();
    }).catch(err => {
      this.logger.error('Failed to update required hours', err);
      this.toastr.error('Bijwerken mislukt. Probeer later opnieuw.');
    });
  }
}
